// My Dashboard — Workspace (company work) bars: My Tasks + Team Tasks, opening the same TaskDrawer as Workspace.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Check, ClipboardList, Hand, Users } from "lucide-react";
import TaskDrawer from "../workspace/panels/TaskDrawer";
import { workspaceApi, RHYTHM_META, doneThisPeriod, tickTask } from "../workspace/_shared";

export function WorkspaceTaskPopup({ taskId, onClose, onSaved }) {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  useEffect(() => {
    if (!taskId) return;
    Promise.all([workspaceApi.listUsers(), workspaceApi.listTeams("all")])
      .then(([u, t]) => { setUsers(u || []); setTeams(t.items || []); }).catch(() => {});
  }, [taskId]);
  return <TaskDrawer open={!!taskId} taskId={taskId} users={users} teams={teams} onClose={onClose} onSaved={onSaved} />;
}

export function RhythmPill({ r }) {
  if (!r || r === "none") return null;
  const m = RHYTHM_META[r];
  return <span className={`shrink-0 px-1.5 py-0.5 rounded-full border text-[9px] font-mono uppercase tracking-wider ${m?.cls || ""}`}>{m?.label || r}</span>;
}

export function SectionHeading({ title, sub, testid }) {
  return (
    <div className="pt-3 flex items-baseline gap-3" data-testid={testid}>
      <h2 className="text-base md:text-lg font-semibold text-white">{title}</h2>
      <span className="text-[12px] text-slate-500">{sub}</span>
    </div>
  );
}

function Who({ t, onClaim, onTick }) {
  if (t.guide) return <span className="inline-flex items-center gap-1 text-amber-200 text-[11px]"><BookOpen className="w-3 h-3" /> Guide</span>;
  if (t.assign_mode === "each") {
    const mine = doneThisPeriod(t.my_done_at, t.recurrence_class);
    return (
      <span className="inline-flex items-center gap-2">
        <span className="text-teal-300 text-[11px] inline-flex items-center gap-1"><Users className="w-3 h-3" /> Everyone</span>
        <button onClick={(e) => { e.stopPropagation(); onTick(t, !mine); }} data-testid={`team-tick-${t.id}`}
          className={`px-2 py-0.5 rounded-full border text-[11px] inline-flex items-center gap-1 ${mine ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-200" : "border-white/15 text-slate-200 hover:bg-white/5"}`}>
          <Check className="w-3 h-3" /> {mine ? "Done" : "Tick"}
        </button>
      </span>
    );
  }
  if (t.claimable) return (
    <button onClick={(e) => { e.stopPropagation(); onClaim(t); }} data-testid={`team-claim-${t.id}`}
      className="px-2 py-0.5 rounded-full border border-teal-400/40 bg-teal-500/10 text-teal-200 text-[11px]">Claim</button>
  );
  return <span className="text-[11px] text-slate-400">{t.owner === "me" ? "You" : "Claimed"}</span>;
}

export function TeamTasksBar({ Bar, Chip, Empty, open, onToggle, rows, onClaim, onOpenTask, onChanged }) {
  const [chip, setChip] = useState("todo");
  const filtered = rows.filter((t) => chip === "all" || (chip === "guides" ? t.guide : !t.guide));
  const tick = async (t, done) => {
    try { await tickTask(t.id, done); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  return (
    <Bar icon={Hand} tone="text-fuchsia-300" title="Team Tasks" count={rows.filter((t) => !t.guide).length} open={open} onToggle={onToggle}
      testid="bar-team" summary={`${rows.filter((t) => t.claimable).length} to claim · ${rows.filter((t) => t.guide).length} guides`}
      toolbar={<>
        <Chip active={chip === "todo"} onClick={() => setChip("todo")} testid="team-chip-todo">To do</Chip>
        <Chip active={chip === "guides"} onClick={() => setChip("guides")} testid="team-chip-guides">Guides</Chip>
        <Chip active={chip === "all"} onClick={() => setChip("all")} testid="team-chip-all">All</Chip>
      </>}
      footer={<><span>{filtered.length} shown</span><Link to="/workspace/teams" className="text-teal-300 hover:underline">Team Tasks in Workspace</Link></>}>
      {filtered.length === 0 ? <Empty>{chip === "guides" ? "No team guides yet." : "Nothing for your teams right now."}</Empty> : (
        <div className="divide-y divide-white/[0.04]">
          {filtered.map((t) => (
            <div key={t.id} onClick={() => onOpenTask(t.id)} data-testid={`team-row-${t.id}`}
              className="flex items-center gap-3 px-3 py-2 hover:bg-white/[0.03] cursor-pointer">
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-slate-100 truncate flex items-center gap-1.5">
                  <RhythmPill r={t.recurrence_class} /><span className="truncate">{t.title}</span>
                </div>
                <div className="text-[10.5px] text-slate-500 font-mono">{t.team}{t.assign_mode === "each" && !t.guide ? ` · ${t.done_count}/${t.team_size} done` : ""}{t.due_date ? ` · due ${t.due_date.slice(0, 10)}` : ""}</div>
              </div>
              <Who t={t} onClaim={onClaim} onTick={tick} />
            </div>
          ))}
        </div>
      )}
    </Bar>
  );
}

export const MyTasksIcon = ClipboardList;
